import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { BarChart } from '@/components/charts/BarChart';
import { LineChart } from '@/components/charts/LineChart';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import {
  courseNameFrom,
  statsEligibleRounds,
  useCourseIndex,
  useHandicap,
  usePlayerProfile,
  usePlayerRounds,
  useRoundDetails,
  useRoundsTogether,
} from '@/lib/api/queries';
import type {
  CourseSummary,
  HeadToHead,
  PlayerProfile as PlayerProfileData,
  Round,
  RoundsTogether,
  RoundSummary,
  SharedRound,
} from '@/lib/api/types';
import { ApiError } from '@/lib/api/errors';
import { cx } from '@/lib/cx';
import { fmtDateLong, fmtMonthYear } from '@/lib/format/date';
import { fmtDecimal, fmtDecimal2, fmtInt, fmtPct, fmtToPar } from '@/lib/format/number';
import { t } from '@/lib/i18n';
import { summariseRounds, withDetailStats, type GameSummary } from '@/lib/stats/aggregate';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { axisMonths, handicapSeries, toParSeries } from '@/lib/stats/series';
import { routes } from '@/routes/paths';
import styles from './PlayerProfile.module.css';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';

/**
 * O perfil de outro jogador (canvas 3a).
 *
 * A tela é sobre ele, mas quase nenhum número dela faz sentido sozinho — daí o handicap dele vir
 * primeiro e o seu ao lado, menor, e o retrospecto de voltas juntos fechar a página. O canvas
 * resume: *"o número só tem sentido comparado"*.
 *
 * ## Como esta tela é construída
 *
 * Ela **não** reusa os blocos genéricos do app (`StatRow`, `SectionBar`, `ChartFrame`). O 3a tem
 * anatomia própria: faixa verde no topo, cartão de handicap com régua, grade de três colunas com
 * divisor vertical, tira de retrospecto. Tentar espremer isso nos componentes de outra tela foi a
 * primeira versão desta página, e o resultado não parecia com o desenho.
 *
 * Os números, esses sim, vêm das mesmas funções puras que calculam os seus — `summariseRounds`,
 * `withDetailStats`, `handicapSeries`. O que muda é de quem é a lista.
 */
export function PlayerProfile() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const profile = usePlayerProfile(userId);
  const rounds = usePlayerRounds(userId);
  const together = useRoundsTogether(userId);
  const mine = useHandicap();
  const courseIndex = useCourseIndex();
  const isDesktop = useIsDesktop();

  const completed = useMemo(() => statsEligibleRounds(rounds.data?.rounds ?? []), [rounds.data]);

  /*
   * Um número de buracos por vez — nunca os dois somados.
   *
   * É a regra que a tela de Stats aplica desde sempre, e ignorá-la produzia números sem sentido:
   * "melhor volta 45" (uma volta de 9) ao lado de "score médio 77,5" (voltas de 18). Média de
   * coisas diferentes não é média. O recorte é o que a pessoa mais joga, e o cabeçalho diz qual é.
   */
  const holeCounts = useMemo(() => {
    const present = new Set(completed.map((r) => r.holesPlayed));
    return ([18, 9] as const).filter((n) => present.has(n));
  }, [completed]);
  const activeHoles = holeCounts[0] ?? 18;

  const eligible = useMemo(
    () => completed.filter((r) => r.holesPlayed === activeHoles),
    [completed, activeHoles],
  );
  const details = useRoundDetails(eligible.map((r) => r.id));
  const detailsReady = eligible.length > 0 && details.loadedCount === eligible.length;
  const summary = useMemo(
    () => withDetailStats(summariseRounds(eligible), detailsReady ? details.rounds : []),
    [eligible, detailsReady, details.rounds],
  );

  /*
   * Um perfil fechado não é rota errada nem pessoa inexistente: é alguém que desligou o
   * compartilhamento. Dizer isso evita que o usuário procure defeito onde há uma escolha.
   */
  if (profile.error instanceof ApiError && profile.error.status === 403) {
    return (
      <div className={styles.page}>
        <BackOnly onBack={() => navigate(-1)} />
        <div className={styles.pad}>
          <EmptyState title={t('player.private.title')}>{t('player.private.body')}</EmptyState>
        </div>
      </div>
    );
  }

  if (profile.isLoading || !profile.data) {
    return (
      <div className={styles.page}>
        <BackOnly onBack={() => navigate(-1)} />
        <div className={styles.pad}>
          <Skeleton height={96} radius={14} />
          <Skeleton height={140} radius={14} style={{ marginTop: 12 }} />
        </div>
      </div>
    );
  }

  const player = profile.data;
  const firstName = player.displayName.trim().split(/\s+/)[0] ?? player.displayName;

  const shared = {
    player,
    firstName,
    mine: mine.data?.handicapIndex ?? null,
    eligible,
    activeHoles,
    summary,
    details: detailsReady ? details.rounds : [],
    detailsReady,
    together: together.data,
    togetherLoading: together.isLoading,
    courseIndex: courseIndex.data,
    onBack: () => navigate(-1),
  };

  /*
   * Dois layouts, não um responsivo.
   *
   * O 3a e o 3b não são a mesma árvore com larguras diferentes: no desktop o handicap vira painel
   * lateral com legenda, as seis estatísticas viram uma linha só, os gráficos ficam lado a lado, e
   * as voltas juntos viram tabela com coluna de diferença. Espremer isso em media query daria um
   * DOM que não serve bem a nenhum dos dois — é a mesma razão de `GroupScorecard` e
   * `GroupScoreGrid` serem dois componentes.
   */
  return isDesktop ? <DesktopProfile {...shared} /> : <MobileProfile {...shared} />;
}

interface ProfileProps {
  player: PlayerProfileData;
  firstName: string;
  mine: number | null;
  eligible: RoundSummary[];
  activeHoles: 9 | 18;
  summary: GameSummary;
  details: Round[];
  detailsReady: boolean;
  together: RoundsTogether | undefined;
  togetherLoading: boolean;
  courseIndex: Map<string, CourseSummary> | undefined;
  onBack: () => void;
}

/** O 3a: uma coluna, para o celular. */
function MobileProfile({
  player,
  firstName,
  mine,
  eligible,
  activeHoles,
  summary,
  details,
  detailsReady,
  together,
  togetherLoading,
  courseIndex,
  onBack,
}: ProfileProps) {
  return (
    <div className={styles.page}>
      <Hero player={player} onBack={onBack} />

      <HandicapCard theirs={player.handicapIndex} basedOn={player.basedOnRounds} mine={mine} />

      <SectionRow
        title={t('player.theirGame')}
        aside={t('player.roundsOver', { count: eligible.length, holes: activeHoles })}
      />

      <div className={styles.statRow}>
        <ScoreStat summary={summary} />
        <BestStat summary={summary} rounds={eligible} />
        <Stat label={t('player.girAverage')} value={fmtPct(summary.averageGirPct)} />
      </div>
      <div className={styles.statRow}>
        <PuttsStat summary={summary} />
        <Stat label={t('player.fairway')} value={fmtPct(summary.averageFairwayPct)} />
        <Stat
          label={t('player.bestDifferential')}
          value={summary.bestDifferential != null ? fmtDecimal(summary.bestDifferential) : null}
        />
      </div>

      <SectionRow
        title={t('player.evolution')}
        action={
          <Link to={routes.playerRounds(player.id)} className={styles.sectionAction}>
            {t('player.seeAll')}
            <ChevronRightIcon size={14} />
          </Link>
        }
      />
      <div className={styles.chartCard}>
        <HandicapChartCard rounds={details} ready={detailsReady} />
      </div>

      <TogetherBlock
        data={together}
        loading={togetherLoading}
        name={firstName}
        playerId={player.id}
        courseIndex={courseIndex}
      />
    </div>
  );
}

/**
 * O 3b: duas colunas, para o desktop.
 *
 * O canvas resume o porquê: *"o desktop mostra o que o mobile precisa esconder — os dois lado a
 * lado"*. O handicap vira painel com legenda, as seis estatísticas cabem numa linha, os dois
 * gráficos convivem, e as voltas juntos viram tabela com o vencedor em peso e a coluna de
 * diferença — que no celular seria uma linha de resumo.
 */
function DesktopProfile({
  player,
  firstName,
  mine,
  eligible,
  activeHoles,
  summary,
  details,
  detailsReady,
  together,
  courseIndex,
  onBack,
}: ProfileProps) {
  const [shown, setShown] = useState(6);
  const played = (together?.rounds ?? []).filter(
    (r) => r.mine.gross != null && r.theirs.gross != null,
  );
  const visible = played.slice(0, shown);

  return (
    <div className={styles.pageWide}>
      <Hero
        player={player}
        onBack={onBack}
        wide
        extra={t('player.roundsOver', { count: eligible.length, holes: activeHoles })}
      />

      <div className={styles.split}>
        <aside className={styles.sidePanel}>
          <div className={styles.microLabel}>{t('player.handicapIndex')}</div>
          <div className={styles.handicapValueWide}>
            {player.handicapIndex != null ? fmtDecimal(player.handicapIndex) : '—'}
          </div>
          <div className={styles.handicapNote}>
            {t('player.basedOn', { count: player.basedOnRounds })}
          </div>

          <div className={styles.sideDivider}>
            <div className={styles.mineRow}>
              <span className={styles.mineLabel}>{t('player.you')}</span>
              <span className={styles.mineValue}>{mine != null ? fmtDecimal(mine) : '—'}</span>
            </div>
            <Ruler theirs={player.handicapIndex} mine={mine} />
            <div className={styles.rulerAxis}>
              <span>{t('player.scratch')}</span>
              <span>36</span>
            </div>
            <div className={styles.rulerLegend}>
              <span className={styles.legendItem}>
                <span className={cx(styles.legendDot, styles.markTheirs)} />
                {firstName}
              </span>
              <span className={styles.legendItem}>
                <span className={cx(styles.legendDot, styles.markMine)} />
                {t('player.you')}
              </span>
            </div>
          </div>
        </aside>

        <div className={styles.sideMain}>
          <div className={styles.statLine}>
            <ScoreStat summary={summary} wide />
            <BestStat summary={summary} rounds={eligible} />
            <Stat label={t('player.girAverage')} value={fmtPct(summary.averageGirPct)} />
            <PuttsStat summary={summary} />
            <Stat label={t('player.fairway')} value={fmtPct(summary.averageFairwayPct)} />
            <Stat
              label={t('player.bestDifferential')}
              value={summary.bestDifferential != null ? fmtDecimal(summary.bestDifferential) : null}
              caption={t('player.countsToIndex')}
            />
          </div>

          <div className={styles.chartGrid}>
            <HandicapChartCard rounds={details} ready={detailsReady} wide />
            <ToParCard rounds={eligible} ready={detailsReady} />
          </div>
        </div>
      </div>

      <div className={styles.bandBar}>
        <span>{t('player.together')}</span>
        <span className={styles.bandAside}>
          {t('player.togetherOfTotal', {
            count: played.length,
            total: player.completedRounds,
          })}
        </span>
      </div>

      <div className={styles.split}>
        <aside className={styles.sidePanel}>
          <div className={styles.microLabel}>{t('player.recordGross')}</div>
          <div className={styles.recordBig}>
            <span className={styles.recordTheirs}>{together?.gross.theirs ?? 0}</span>
            <span className={styles.recordDash}>–</span>
            <span className={styles.recordMine}>{together?.gross.mine ?? 0}</span>
            <span className={styles.recordTies}>
              {t('player.tiesCount', { count: together?.gross.ties ?? 0 })}
            </span>
          </div>

          {together && <Strip tally={together.gross} wide />}

          <div className={styles.stripLegend}>
            <span className={styles.legendItem}>
              <span className={cx(styles.legendSquare, styles.stripTheirs)} />
              {firstName}
            </span>
            <span className={styles.legendItem}>
              <span className={cx(styles.legendSquare, styles.stripMine)} />
              {t('player.you')}
            </span>
            <span className={styles.legendItem}>
              <span className={cx(styles.legendSquare, styles.stripTie)} />
              {t('player.tieLabel')}
            </span>
          </div>

          {together && together.net.played > 0 && (
            <p className={styles.netNote}>
              {t('player.netFlips', {
                theirs: together.net.theirs,
                mine: together.net.mine,
              })}
            </p>
          )}
        </aside>

        <div className={styles.sideMain}>
          <div className={styles.tableHead}>
            <span className={styles.colWhere}>{t('player.dateAndCourse')}</span>
            <span className={styles.colScore}>{firstName}</span>
            <span className={styles.colScore}>{t('player.you')}</span>
            <span className={styles.colDiff}>{t('player.difference')}</span>
            <span className={styles.colOpen} />
          </div>

          {visible.map((round) => {
            const theyWon = (round.theirs.gross ?? 0) < (round.mine.gross ?? 0);
            const tie = round.theirs.gross === round.mine.gross;
            return (
              <div key={round.groupRoundId} className={styles.tableRow}>
                <div className={styles.colWhere}>
                  <div className={styles.rowDate}>{fmtDateLong(round.playedAt)}</div>
                  <div className={styles.rowWhere}>
                    {courseNameFrom(courseIndex, round.courseId)}
                    {round.theirs.teeBoxName ? ` · ${round.theirs.teeBoxName}` : ''}
                  </div>
                </div>
                {/* O vencedor em peso: é assim que a tabela se lê sem somar nada. */}
                <span className={cx(styles.colScore, theyWon && !tie ? styles.won : styles.lost)}>
                  {fmtInt(round.theirs.gross)}
                </span>
                <span className={cx(styles.colScore, !theyWon && !tie ? styles.won : styles.lost)}>
                  {fmtInt(round.mine.gross)}
                </span>
                <span className={styles.colDiff}>{differenceOf(round, firstName)}</span>
                {/* Sem volta dele carregada não há o que abrir — o rótulo some em vez de virar link morto. */}
                {round.theirs.roundId ? (
                  <Link to={routes.round(round.theirs.roundId)} className={styles.colOpen}>
                    {t('player.open')}
                  </Link>
                ) : (
                  <span className={styles.colOpen} />
                )}
              </div>
            );
          })}

          {played.length > 0 && (
            <div className={styles.tableFoot}>
              <span className={styles.tableFootCount}>
                {t('player.showing', { shown: visible.length, total: played.length })}
              </span>
              {shown < played.length && (
                <button
                  type="button"
                  className={styles.loadMore}
                  onClick={() => setShown((n) => n + 6)}
                >
                  {t('player.loadMore', { count: Math.min(6, played.length - shown) })}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** A faixa verde do topo. No desktop ela é mais alta e traz os chips na mesma linha do nome. */
function Hero({
  player,
  onBack,
  wide = false,
  extra,
}: {
  player: PlayerProfileData;
  onBack: () => void;
  wide?: boolean;
  extra?: string;
}) {
  return (
    <header className={cx(styles.hero, wide && styles.heroWide)}>
      {!wide && (
        <div className={styles.heroTop}>
          <button
            type="button"
            className={styles.heroIcon}
            onClick={onBack}
            aria-label={t('rounds.back')}
          >
            <ChevronLeftIcon size={20} />
          </button>
        </div>
      )}

      <div className={styles.heroWho}>
        <span className={styles.heroAvatar} aria-hidden="true">
          {initialsOf(player.displayName)}
        </span>
        <div className={styles.heroBody}>
          <h1 className={styles.heroName}>{player.displayName}</h1>
          <p className={styles.heroSince}>
            {t('player.memberSince', { when: fmtMonthYear(player.memberSince) })}
            {player.homeCourseName ? ` · ${player.homeCourseName}` : ''}
            {extra ? ` · ${extra}` : ''}
          </p>
        </div>
        {player.friendsSince && (
          <div className={styles.heroChips}>
            <span className={styles.chipFilled}>{t('player.friends')}</span>
          </div>
        )}
      </div>
    </header>
  );
}

function BackOnly({ onBack }: { onBack: () => void }) {
  return (
    <div className={styles.hero}>
      <div className={styles.heroTop}>
        <button
          type="button"
          className={styles.heroIcon}
          onClick={onBack}
          aria-label={t('rounds.back')}
        >
          <ChevronLeftIcon size={20} />
        </button>
      </div>
    </div>
  );
}

/**
 * O handicap dele, o seu ao lado, e a régua que põe os dois na mesma escala.
 *
 * A régua vai de scratch a 36 porque é a faixa em que o handicap amador vive, e o canvas a escolhe
 * para evitar um gráfico: duas marcas numa linha dizem "quanto melhor" sem eixo e sem legenda. A
 * marca dele é cheia, a sua é um anel — a mesma distinção do desenho, e a razão de não precisar de
 * rótulo sob cada ponto.
 */
function HandicapCard({
  theirs,
  basedOn,
  mine,
}: {
  theirs: number | null;
  basedOn: number;
  mine: number | null;
}) {
  const gap = theirs != null && mine != null ? mine - theirs : null;

  return (
    <section className={styles.handicap}>
      <div className={styles.handicapRow}>
        <div>
          <div className={styles.microLabel}>{t('player.handicapIndex')}</div>
          <div className={styles.handicapValue}>{theirs != null ? fmtDecimal(theirs) : '—'}</div>
          <div className={styles.handicapNote}>{t('player.basedOn', { count: basedOn })}</div>
        </div>

        {mine != null ? (
          <div className={styles.handicapMine}>
            <div className={styles.microLabel}>{t('player.you')}</div>
            <div className={styles.handicapMineValue}>{fmtDecimal(mine)}</div>
            {gap != null && gap !== 0 && (
              <div className={styles.handicapGap}>
                {gap > 0
                  ? t('player.strokesBetter', { value: fmtDecimal(Math.abs(gap)) })
                  : t('player.strokesWorse', { value: fmtDecimal(Math.abs(gap)) })}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.handicapMine}>
            <div className={styles.handicapGap}>{t('player.noIndexYet')}</div>
          </div>
        )}
      </div>

      <div className={styles.ruler}>
        <Ruler theirs={theirs} mine={mine} />
      </div>
      <div className={styles.rulerAxis}>
        <span>{t('player.scratch')}</span>
        <span>36</span>
      </div>
    </section>
  );
}

/** O cartão de evolução do 3a: título, a leitura em uma linha, e a série. */
function HandicapChartCard({
  rounds,
  ready,
  wide = false,
}: {
  rounds: Round[];
  ready: boolean;
  wide?: boolean;
}) {
  const points = handicapSeries(rounds);
  const from = points[0];
  const to = points[points.length - 1];
  const first = from?.value;
  const last = to?.value;
  const labels = points.length >= 2 ? axisMonths(points) : [];

  return (
    <section>
      <div className={styles.chartHead}>
        <span className={styles.chartTitle}>
          {wide ? t('player.handicapEvolution') : t('player.handicap')}
        </span>
        {points.length >= 2 && from && to && first != null && last != null && (
          <span className={styles.chartAside}>
            {fmtDecimal(first)} →{' '}
            <strong className={styles.chartStrong}>{fmtDecimal(last)}</strong>{' '}
            {/* No desktop os dois gráficos dividem a linha; o "em N meses" não cabe ao lado. */}
            {!wide && ' ' + t('player.inMonths', { count: monthsBetween(from.at, to.at) })}
          </span>
        )}
      </div>

      <div className={styles.chartBox}>
        {!ready ? (
          <Skeleton height={104} radius={8} />
        ) : points.length < 2 ? (
          <p className={styles.chartEmpty}>{t('player.notEnoughForChart')}</p>
        ) : (
          <>
            <LineChart
              ariaLabel={t('player.handicapAria', {
                from: fmtDecimal(first),
                to: fmtDecimal(last),
              })}
              series={[
                {
                  values: points.map((p) => p.value),
                  color: 'var(--green-mid)',
                  area: true,
                  dot: (i: number) => (i === points.length - 1 ? { r: 3.2, fill: 'var(--green-mid)' } : null),
                },
              ]}
            />
            {labels.length > 0 && (
              <div className={styles.chartAxis}>
                <span>{labels[0]}</span>
                <span>{labels[labels.length - 1]}</span>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * "Score para o par" — o segundo gráfico do 3b.
 *
 * Uma barra por volta e a linha tracejada da média. A cor não carrega juízo: o eixo já diz quem
 * ficou acima do par, e pintar de vermelho seria repetir a informação com um veredito.
 */
function ToParCard({ rounds, ready }: { rounds: RoundSummary[]; ready: boolean }) {
  const points = toParSeries(rounds);
  const mean =
    points.length > 0 ? points.reduce((a, p) => a + p.value, 0) / points.length : null;

  return (
    <section>
      <div className={styles.chartHead}>
        <span className={styles.chartTitle}>{t('player.toPar')}</span>
        {mean != null && (
          <span className={styles.chartAside}>
            {t('player.meanAside', { value: fmtToPar(Math.round(mean)) })}
          </span>
        )}
      </div>
      <div className={styles.chartBox}>
        {!ready ? (
          <Skeleton height={104} radius={8} />
        ) : points.length === 0 ? (
          <p className={styles.chartEmpty}>{t('player.notEnoughForChart')}</p>
        ) : (
          <BarChart
            bars={points.map((p) => ({
              key: p.id,
              value: p.value,
              display: fmtToPar(p.value),
              label: '',
              tone: 'plain' as const,
            }))}
            height={104}
            referenceValue={mean ?? undefined}
            ariaLabel={t('player.toParAria', { count: points.length })}
            compact
          />
        )}
      </div>
    </section>
  );
}

/**
 * Voltas juntos: o retrospecto e as últimas partidas.
 *
 * A tira de dez marcas é a peça que o canvas destaca — *"resume o retrospecto antes de qualquer
 * linha de lista"*. Vitória dele em cinza, sua em verde, empate no tom mais claro.
 */
function TogetherBlock({
  data,
  loading,
  name,
  playerId,
  courseIndex,
}: {
  data: RoundsTogether | undefined;
  loading: boolean;
  name: string;
  playerId: string;
  courseIndex: Map<string, CourseSummary> | undefined;
}) {
  if (loading) {
    return (
      <div className={styles.pad}>
        <Skeleton height={110} radius={12} />
      </div>
    );
  }
  if (!data || data.rounds.length === 0) return null;

  const played = data.rounds.filter((r) => r.mine.gross != null && r.theirs.gross != null);
  const recent = played.slice(0, 3);

  return (
    <>
      <SectionRow
        title={t('player.together')}
        aside={t('player.togetherCount', { count: data.rounds.length })}
      />

      <div className={styles.record}>
        <div>
          <div className={styles.recordLead}>
            {t('player.tookIt', { name, count: data.gross.theirs })}
          </div>
          <div className={styles.recordRest}>
            {t('player.recordRest', { mine: data.gross.mine, ties: data.gross.ties })}
          </div>
          {data.net.played > 0 && (
            <div className={styles.recordRest}>
              {t('player.recordNet', {
                name,
                theirs: data.net.theirs,
                mine: data.net.mine,
              })}
            </div>
          )}
        </div>
        <Strip tally={data.gross} />
      </div>

      <ul>
        {recent.map((round) => (
          <li key={round.groupRoundId} className={styles.row}>
            <div className={styles.rowBody}>
              <div className={styles.rowDate}>{fmtDateLong(round.playedAt)}</div>
              <div className={styles.rowWhere}>
                {courseNameFrom(courseIndex, round.courseId)}
                {round.theirs.teeBoxName ? ` · ${round.theirs.teeBoxName}` : ''}
              </div>
              <span className={styles.rowChip}>{outcomeOf(round, name)}</span>
            </div>
            <div className={styles.rowScore}>
              <span className={styles.rowStrokes}>{fmtInt(round.theirs.gross)}</span>
              <span className={styles.rowToPar}>{fmtToPar(round.theirs.toPar)}</span>
            </div>
          </li>
        ))}
      </ul>

      {data.rounds.length > recent.length && (
        <div className={styles.pad}>
          <Link to={routes.playerRounds(playerId)} className={styles.seeAllButton}>
            {t('player.seeTogether', { count: data.rounds.length })}
          </Link>
        </div>
      )}
    </>
  );
}

/** Dez marcas, uma por volta: o retrospecto antes de qualquer linha de lista. */
function Strip({ tally, wide = false }: { tally: HeadToHead; wide?: boolean }) {
  const marks = [
    ...Array<'theirs'>(tally.theirs).fill('theirs'),
    ...Array<'mine'>(tally.mine).fill('mine'),
    ...Array<'tie'>(tally.ties).fill('tie'),
  ];
  if (marks.length === 0) return null;

  return (
    <div className={cx(styles.strip, wide && styles.stripWide)} aria-hidden="true">
      {marks.map((kind, i) => (
        <span
          key={i}
          className={cx(
            styles.stripMark,
            kind === 'theirs' && styles.stripTheirs,
            kind === 'mine' && styles.stripMine,
            kind === 'tie' && styles.stripTie,
          )}
        />
      ))}
    </div>
  );
}

/**
 * A régua de scratch a 36.
 *
 * Marca cheia para ele, anel para você — é essa distinção que dispensa rótulo sob cada ponto, e no
 * desktop ela ganha legenda ao lado, como o 3b desenha.
 */
function Ruler({ theirs, mine }: { theirs: number | null; mine: number | null }) {
  const at = (value: number) => `${Math.min(100, Math.max(0, (value / 36) * 100))}%`;
  return (
    <div className={styles.rulerTrack} role="presentation">
      {theirs != null && (
        <span className={cx(styles.mark, styles.markTheirs)} style={{ left: at(theirs) }} />
      )}
      {mine != null && (
        <span className={cx(styles.mark, styles.markMine)} style={{ left: at(mine) }} />
      )}
    </div>
  );
}

/* Os três blocos que têm legenda embaixo, compartilhados pelos dois layouts. */

function ScoreStat({ summary, wide = false }: { summary: GameSummary; wide?: boolean }) {
  return (
    <Stat
      label={t('player.averageScore')}
      value={summary.averageScore != null ? fmtDecimal(summary.averageScore) : null}
      caption={
        summary.averageToPar != null
          ? wide
            ? t('player.toParCaption', { value: fmtToPar(Math.round(summary.averageToPar)) })
            : fmtToPar(Math.round(summary.averageToPar))
          : undefined
      }
    />
  );
}

function BestStat({ summary, rounds }: { summary: GameSummary; rounds: RoundSummary[] }) {
  return (
    <Stat
      label={t('player.bestRound')}
      value={fmtInt(summary.bestScore)}
      caption={bestRoundWhen(rounds, summary.bestScore)}
    />
  );
}

function PuttsStat({ summary }: { summary: GameSummary }) {
  return (
    <Stat
      label={t('player.puttsPerRound')}
      value={summary.averagePuttsPerRound != null ? fmtDecimal(summary.averagePuttsPerRound) : null}
      caption={
        summary.averagePuttsPerHole != null
          ? t('player.perHole', { value: fmtDecimal2(summary.averagePuttsPerHole) })
          : undefined
      }
    />
  );
}

/** "Bruno +7" — quem levou e por quanto. Empate não ganha nome. */
function differenceOf(round: SharedRound, name: string): string {
  const theirs = round.theirs.gross;
  const mine = round.mine.gross;
  if (theirs == null || mine == null) return '—';
  if (theirs === mine) return t('player.tieLabel');
  return theirs < mine
    ? t('player.aheadBy', { name, value: mine - theirs })
    : t('player.aheadBy', { name: t('player.you'), value: theirs - mine });
}

function SectionRow({
  title,
  aside,
  action,
}: {
  title: string;
  aside?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={styles.sectionRow}>
      <span className={styles.sectionTitle}>{title}</span>
      {action ?? (aside && <span className={styles.sectionAside}>{aside}</span>)}
    </div>
  );
}

function Stat({
  label,
  value,
  caption,
}: {
  label: string;
  value: string | null;
  caption?: string;
}) {
  return (
    <div className={styles.stat}>
      <div className={styles.microLabel}>{label}</div>
      <div className={cx(styles.statValue, value == null && styles.statNull)}>{value ?? '—'}</div>
      {caption && <div className={styles.statCaption}>{caption}</div>}
    </div>
  );
}

/** "Ele levou · você 84" — o chip que diz quem levou e com quanto, na linha da volta. */
function outcomeOf(round: SharedRound, name: string): string {
  const theirs = round.theirs.gross;
  const mine = round.mine.gross;
  if (theirs == null || mine == null) return '—';
  if (theirs === mine) return t('player.chipTie', { value: mine });
  return theirs < mine
    ? t('player.chipTheyWon', { name, value: mine })
    : t('player.chipYouWon', { value: mine });
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Quantos meses a série cobre — o "em 12 meses" do canvas.
 *
 * O desenho mede **tempo**, não quantidade de voltas: "caiu 2,6 em 12 meses" diz algo sobre o
 * jogador; "em 24 voltas" diz sobre o uso do app. Mínimo de 1 para não escrever "em 0 meses"
 * quando tudo aconteceu no mesmo mês.
 */
function monthsBetween(from: string, to: string): number {
  const a = new Date(from);
  const b = new Date(to);
  const months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  return Math.max(1, months);
}

/** O mês da melhor volta — o "73 · jul 2026" do canvas. */
function bestRoundWhen(
  rounds: { totalStrokes: number | null; completedAt: string | null; startedAt: string }[],
  best: number | null,
): string | undefined {
  if (best == null) return undefined;
  const match = rounds.find((r) => r.totalStrokes === best);
  return match ? fmtMonthYear(match.completedAt ?? match.startedAt) : undefined;
}
